import { Component, OnInit, Input, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { AuthService } from '../shared/services/auth.service';
import { ApiService } from '../shared/services/api.service';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DatosDenuncia } from 'src/app/shared/models/datosDenuncia';
import { environment } from "../../environments/environment.prod"
import { Usuarios } from '../dashboard/models/usuarios';
import { MatSelect, MatSelectChange } from '@angular/material/select';

@Component({
  selector: 'app-validar-denuncia',
  templateUrl: './validar-denuncia.component.html',
  styleUrls: ['./validar-denuncia.component.css']
})
export class ValidarDenunciaComponent implements OnInit {
  assetsUrl: string = environment.baseAssetsUrl;

  // Reactive form group for complaint validation.
  validatingForm: FormGroup;
  // Controls the visibility of the validation form.
  IsHiddenForm: boolean = false;
  // List of all compliance users available for selection.
  allCompliances: Usuarios[] = [];
  // List of compliance users filtered for selection.
  compliances: Usuarios[] = [];
  // List of compliance users available to be declined.
  compliancesToDecline: Usuarios[] = [];
  // Default compliance user selected by the user.
  nameComplianceDefault: string = '';
  // Randomly selected compliance user.
  nameComplianceRandom: string = '';
  // Final compliance user selected for validation.
  nameComplianceFinal: string = '';
  // Indicates if a compliance user has been selected.
  isComplianceSelected: boolean = false;
  // Auxiliary flag for selection logic.
  flag1: boolean = false;
  // Stores conflict management status from local storage.
  gestionarConflicto: string = '';
  // Indicates if the initial selection state is active.
  inicial: boolean = false;
  // Indicates if the personalized ID feature is active.
  isActiveIdPersonalizable: boolean = false;
  // Role ID for compliance users (default: '4').
  idUserCompliance: string = '4';
  //  List of compliance users that have been declined.
  compliancesDeclined: any[] = [];
  // Complaint data input from parent component.
  @Input() denuncia: DatosDenuncia;
  // Reference to the compliance select dropdown in the template.
  @ViewChild('complianceSelect') complianceSelect!: MatSelect;

  constructor(
    private apiService: ApiService,
    private auth: AuthService,
    private router: Router,
    private toastr: ToastrService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    const language = this.auth.getLanguage();
    this.translate.setDefaultLang(language || 'es');

    this.validatingForm = new FormGroup({
      validarDenuncia: new FormControl('', []),
      userCompliance: new FormControl('', [Validators.required]),
      compliancesToDecline: new FormControl('')
    });

    this.isActiveIdPersonalizable = this.auth.getIdPersonalizable() === '1';
    this.gestionarConflicto = localStorage.getItem('conflictCompliance') || '';
    this.fetchAllCompliances();
  }

  /** Fetch all compliance users and select a random one */
  fetchAllCompliances(): void {
    this.fetchComplianceUsers(this.idUserCompliance).subscribe(
      (usuarios: Usuarios[]) => {
        if (usuarios.length > 0) {
          this.allCompliances = usuarios.filter(
            u => u.id_user !== this.denuncia[0].id_receptor_rechazado
          );
          if (!this.isComplianceSelected) {
            this.selectedComplianceUser();
          }
        }
      },
      () => {
        this.toastr.error(this.translate.instant('texto_859'), this.translate.instant('texto_813'));
      }
    );
  }

  /** Returns form control for validarDenuncia */
  get validarDenuncia() {
    return this.validatingForm.get('validarDenuncia');
  }

  /** Returns form control for userCompliance */
  get userCompliance() {
    return this.validatingForm.get('userCompliance');
  }

  /** Handle change of compliance select */
  onChangeUserCompliance(event: MatSelectChange): void {
    this.nameComplianceDefault = event.value;
    this.nameComplianceFinal = this.nameComplianceDefault;
  }

  /** Wrapper for API call */
  fetchComplianceUsers(idUserCompliance: string) {
    return this.apiService.searchUserByIdSocietyRole(this.auth.getSociedad(), idUserCompliance, false, "nulo");
  }

  /** Select random compliance user */
  selectedComplianceUser(): void {
    if (this.allCompliances.length === 0) {
      this.allCompliances = [];
      return;
    }

    const randomIndex = Math.floor(Math.random() * this.allCompliances.length);
    this.nameComplianceRandom = this.allCompliances[randomIndex]?.id_user || '';
    this.nameComplianceFinal = this.nameComplianceRandom;
    this.isComplianceSelected = true;
    this.filterComplianceDeclined();
  }

  /** Add random compliance to declined list */
  showFirstComplianceDeclined(): void {
    this.compliancesDeclined.push(this.nameComplianceRandom);
  }

  /** Filter out selected and rejected compliance from list */
  filterComplianceDeclined(): void {
    this.compliances = this.allCompliances.filter(user =>
      user.id_user !== this.nameComplianceRandom &&
      user.id_user !== this.denuncia[0].id_receptor_rechazado
    );

    if (this.compliances.length === 0) {
      this.compliances = [];
    }
  }

  /** Select compliance manually and filter */
  chooseComplianceBySelect(): void {
    this.inicial = true;
    this.flag1 = true;
    this.nameComplianceFinal = this.nameComplianceDefault;
    this.filterComplianceDeclined();

    if (this.compliances.length === 0) {
      this.toastr.error(this.translate.instant('texto_891'), this.translate.instant('texto_813'));
    }

  }


  /**
   * Filters the list of users to decline based on the selected compliance user.
   * If no compliance user is selected, it uses the default or random compliance user.
   * Updates the compliancesToDecline array and shows an error if the list is empty.
   */
  chooseCompliancesDeclinedBySelect(): void {
    if (!this.nameComplianceFinal) {
      this.nameComplianceFinal = this.nameComplianceDefault || this.nameComplianceRandom;
    }

    this.compliancesToDecline = this.compliances.filter(user =>
      user.id_user !== this.nameComplianceFinal
    );

    if (this.compliancesToDecline.length === 0) {
      this.toastr.error(this.translate.instant('texto_891'), this.translate.instant('texto_813'));
    }
  }


  /**
   * Stores the list of rejected compliances.
   * Adds the random compliance user to the declined list.
   * @param event MatSelectChange event containing the selected declined compliances.
   */
  addComplianceDeclined(event: MatSelectChange): void {
    this.compliancesDeclined = [...event.value, this.nameComplianceRandom];
  }


  /**
   * Re-selects a random compliance user and resets the form state.
   * Clears the default compliance selection, resets flags, and updates the random compliance user.
   */
  chooseComplianceRandom(): void {
    this.inicial = false;
    this.flag1 = false;
    this.nameComplianceDefault = '';

    if (this.complianceSelect) {
      this.complianceSelect.value = null;
    }

    // Reset the declined compliances list when selecting a new random compliance
    this.compliancesDeclined = [];
    if (this.allCompliances.length === 0) {
      this.nameComplianceRandom = '';
      this.nameComplianceFinal = '';
      this.isComplianceSelected = false;
      this.compliances = [];
      return;
    }

    const randomIndex = Math.floor(Math.random() * this.allCompliances.length);
    this.nameComplianceRandom = this.allCompliances[randomIndex]?.id_user || '';
    this.nameComplianceFinal = this.nameComplianceRandom;
    this.isComplianceSelected = true;

    this.filterComplianceDeclined(); // Update the compliances list based on the new random compliance selection
  }


  /**
   * Submits the complaint for validation.
   * Updates the complaint status, assigns the selected compliance user, and sends notification emails.
   * Navigates to the complaint edit page after processing.
   */
  validar(): void {
    const nameComplianceDecline = this.flag1 ? this.nameComplianceRandom : this.nameComplianceDefault;
    const $clau = localStorage.getItem("clausulas_correo") === '1';
    const url = environment.base + "/ver-denuncia/consultar-denuncia";
    const idDenunciaMostrado = this.isActiveIdPersonalizable
      ? this.denuncia[0].identificador_denuncia || this.denuncia[0].id_denuncia
      : this.denuncia[0].id_denuncia;

    if (this.validarDenuncia?.value && this.nameComplianceFinal) {
      this.apiService.updateComplaintById(this.denuncia[0].id_denuncia, "1", this.auth.getSociedad(), "", "").subscribe(
        res => {
          if (res === 200) {
            const compliancesDeclined = this.compliancesDeclined.join(',');
            this.apiService.insertUserComplianceByComplaint(this.denuncia[0].id_denuncia, this.auth.getSociedad(), this.nameComplianceFinal, compliancesDeclined).subscribe(
              (res: any) => {
                if (res.code === 200) {
                  const message = `${this.translate.instant('texto_576')} ${this.nameComplianceFinal} ${this.translate.instant('texto_862')} ${idDenunciaMostrado}`;
                  this.apiService.sendEmail(
                    this.nameComplianceFinal,
                    `${this.translate.instant('texto_621')}${this.denuncia[0].id_denuncia}`,
                    message,
                    url,
                    $clau,
                    this.translate.instant('texto_621'),
                    'gestor'
                  ).subscribe();
                  this.toastr.success(this.translate.instant('texto_191'), this.translate.instant('texto_192'));
                } else {
                  this.toastr.error(this.translate.instant('texto_861'), this.translate.instant('texto_813'));
                }
              });

            if (this.denuncia[0].denunciante_correo !== "") {
              const message = `${this.translate.instant('texto_576')} ${this.denuncia[0].denunciante_nombre} ${this.denuncia[0].denunciante_apellidos}${this.translate.instant('texto_604')} ${idDenunciaMostrado} ${this.translate.instant('texto_650')}`;
              this.apiService.sendEmail(
                this.denuncia[0].denunciante_correo,
                `${this.translate.instant('texto_651')}${this.denuncia[0].id_denuncia}`,
                message,
                url,
                $clau,
                this.translate.instant('texto_651'),
                'gestor'
              ).subscribe();
            }
          } else {
            this.toastr.error(this.translate.instant('texto_196'), this.translate.instant('texto_192'));
          }

          this.router.navigateByUrl('/RefreshComponent', { skipLocationChange: true }).then(() => {
            this.router.navigate(['editar-denuncia/', this.denuncia[0].id_denuncia]);
          });
        });
    }
  }
}
